// src/components/PurposeModal.jsx
// ─────────────────────────────────────────────────────────────
//  Full-screen modal showing Sheepheard's purpose statement.
//  Shared between LoginScreen and Settings.
//  Sub-sections ("What We Pursue" / "Disclaimer") open as
//  full-screen panels (internal view state) — no collapsible.
//  Privacy Policy opens as a stacked modal.
//  Fonts scaled up ~2× for mobile readability.
//  Scroll bug fixed: flex column + minHeight:0 + overflow:scroll.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowLeft, ChevronRight } from 'lucide-react';
import appIcon from '/icons/icon-512.png';
import PrivacyPolicyModal from '@/components/shared/PrivacyPolicyModal';

// ── Commitment card ────────────────────────────────────────────
function CommitmentCard({ emoji, title, description }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '14px',
      padding: '14px', borderRadius: '12px',
      background: '#f0faf0', border: '1px solid #d6e4d0',
    }}>
      <span style={{ fontSize: '1.7rem', lineHeight: 1, marginTop: '2px', flexShrink: 0 }}>{emoji}</span>
      <div>
        <p style={{ margin: '0 0 5px', fontSize: '1.05rem', fontWeight: '700', color: '#14532d', fontFamily: 'Georgia, serif' }}>
          {title}
        </p>
        <p style={{ margin: 0, fontSize: '0.97rem', color: '#4a6741', lineHeight: '1.6' }}>
          {description}
        </p>
      </div>
    </div>
  );
}

// ── Tappable nav card (replaces CollapsibleSection) ────────────
function NavCard({ icon, label, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 18px',
        background: '#ffffff',
        border: '1px solid #d6e4d0',
        borderRadius: '14px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        gap: '12px',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {icon && <span style={{ fontSize: '1.3rem' }}>{icon}</span>}
        <span>
          <span style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: '800',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#166534',
          }}>{label}</span>
          {subtitle && (
            <span style={{ display: 'block', fontSize: '0.88rem', color: '#7aab6e', marginTop: '2px' }}>
              {subtitle}
            </span>
          )}
        </span>
      </span>
      <ChevronRight size={18} color="#4d7c0f" style={{ flexShrink: 0 }} />
    </button>
  );
}

// ── "What We Pursue" full-screen panel ────────────────────────
function PursueView({ onBack }) {
  return (
    <>
      {/* Sub-header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '14px 18px',
        background: '#ffffff',
        borderBottom: '1px solid #d6e4d0',
        flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            background: '#f0f4ef', border: 'none', borderRadius: '50%',
            width: '34px', height: '34px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#166534', flexShrink: 0,
          }}
        >
          <ArrowLeft size={17} />
        </button>
        <div>
          <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#14532d' }}>
            What We Pursue
          </p>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#7aab6e' }}>Three Core Commitments</p>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{
        flex: 1, minHeight: 0,
        overflowY: 'scroll',
        WebkitOverflowScrolling: 'touch',
        padding: '20px 16px 36px',
        display: 'flex', flexDirection: 'column', gap: '14px',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <CommitmentCard emoji="🙏" title="Intimacy with God"
            description="Nurturing a personal, listening relationship with the Shepherd through prayer, Scripture, and devotion." />
          <CommitmentCard emoji="🌿" title="Growth to Maturity"
            description="Tracking spiritual progress with honesty and grace, celebrating every step toward Christlikeness." />
          <CommitmentCard emoji="🤝" title="Community & Care"
            description="Fostering genuine accountability and love between believers so that all may be found and known." />
        </div>

        <div style={{
          padding: '14px 16px', borderRadius: '12px',
          background: '#f7fbf5', border: '1px solid #d6e4d0',
        }}>
          <p style={{ margin: 0, fontSize: '0.97rem', lineHeight: '1.7', color: '#4a5e45', fontStyle: 'italic' }}>
            Sheepheard is a tool in the hands of the Holy Spirit — not a replacement for the
            local church, pastoral counsel, or the living Word of God, but a faithful companion
            that keeps the rhythms of discipleship visible and the voice of the Shepherd near.
          </p>
        </div>

        <div style={{ textAlign: 'center', paddingTop: '8px' }}>
          <p style={{
            margin: '0 0 4px', fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '1rem', color: '#4a6741',
          }}>
            "He restores my soul." — Psalm 23:3
          </p>
        </div>
      </div>
    </>
  );
}

// ── "Disclaimer" full-screen panel ────────────────────────────
function DisclaimerView({ onBack, onOpenPrivacy }) {
  const disclaimers = [
    { bold: 'Not a substitute for Scripture or the Church.', text: "Sheepheard supports engagement with God's Word but does not replace the Bible, participation in a local church, or the guidance of ordained pastors." },
    { bold: 'Not professional counsel.', text: 'Nothing here constitutes psychological, medical, legal, or pastoral advice. Please seek qualified help in a crisis.' },
    { bold: 'Community interactions.', text: 'All sharing should reflect the love befitting the Body of Christ. Users are responsible for what they share.' },
    { bold: 'Progress tracking.', text: "Metrics reflect personal activity, not spiritual standing before God, whose grace alone declares us righteous." },
    { bold: 'Theological positions.', text: 'Sheepheard serves a broad range of evangelical traditions and does not represent any single denomination exclusively.' },
  ];

  return (
    <>
      {/* Sub-header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '14px 18px',
        background: '#ffffff',
        borderBottom: '1px solid #d6e4d0',
        flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          aria-label="Go back"
          style={{
            background: '#f0f4ef', border: 'none', borderRadius: '50%',
            width: '34px', height: '34px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#166534', flexShrink: 0,
          }}
        >
          <ArrowLeft size={17} />
        </button>
        <div>
          <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#14532d' }}>
            Disclaimer
          </p>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#b45309' }}>Please Read Carefully</p>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{
        flex: 1, minHeight: 0,
        overflowY: 'scroll',
        WebkitOverflowScrolling: 'touch',
        padding: '20px 16px 36px',
        display: 'flex', flexDirection: 'column', gap: '10px',
      }}>
        {disclaimers.map(({ bold, text }, i) => (
          <div key={i} style={{
            padding: '13px 15px', borderRadius: '12px',
            background: '#fffdf7', border: '1px solid #fde68a',
          }}>
            <p style={{ margin: 0, fontSize: '0.97rem', lineHeight: '1.65', color: '#78350f' }}>
              <strong style={{ color: '#92400e' }}>{bold}</strong>{' '}{text}
            </p>
          </div>
        ))}

        {/* Privacy link card */}
        <div style={{
          padding: '13px 15px', borderRadius: '12px',
          background: '#fffdf7', border: '1px solid #fde68a',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: '0.97rem', lineHeight: '1.65', color: '#78350f' }}>
            <strong style={{ color: '#92400e' }}>Privacy.</strong>{' '}
            We handle your data with care. Your prayer entries, journals, and records belong to you.
          </p>
          <button
            onClick={onOpenPrivacy}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'none', border: '1px solid #166534',
              borderRadius: '8px', padding: '8px 14px',
              cursor: 'pointer', color: '#166534',
              fontWeight: '700', fontSize: '0.93rem',
              fontFamily: 'inherit',
            }}
          >
            Read our full Privacy Policy
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Modal ─────────────────────────────────────────────────
export default function PurposeModal({ onClose, closeLabel = 'Close' }) {
  const overlayRef = useRef(null);
  const [view, setView] = useState('main'); // 'main' | 'pursue' | 'disclaimer'
  const [showPrivacy, setShowPrivacy] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const pStyle = { fontSize: '1rem', lineHeight: '1.75', color: '#3d5438', margin: '0 0 10px' };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10, 30, 15, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'stretch', justifyContent: 'center',
        fontFamily: "'Manrope', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      {/* Sheet — full height, fixed flex column */}
      <div style={{
        width: '100%', maxWidth: '600px',
        height: '100%',
        background: '#f7fbf5',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 0 40px rgba(10,40,20,0.3)',
      }}>

        {/* ── Sticky main header (always visible) ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px 12px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          background: 'linear-gradient(160deg, #14532d 0%, #1a4228 100%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src={appIcon}
              alt="Sheepheard"
              style={{ width: 34, height: 34, borderRadius: '8px', objectFit: 'contain' }}
            />
            <div>
              <p style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: 'white', fontFamily: 'Georgia, serif' }}>
                Our Purpose
              </p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}>
                Why Sheepheard Exists
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* John 10:27 banner — only on main view */}
        {view === 'main' && (
          <div style={{
            background: 'linear-gradient(160deg, #1a4228 0%, #14532d 100%)',
            padding: '10px 18px 16px',
            textAlign: 'center',
            flexShrink: 0,
          }}>
            <p style={{
              margin: 0, fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '0.95rem', color: 'rgba(255,255,255,0.8)', lineHeight: '1.6',
            }}>
              "My sheep hear my voice, and I know them, and they follow me."
            </p>
            <p style={{
              margin: '4px 0 0', fontSize: '0.82rem',
              color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em',
            }}>
              — John 10:27 ESV
            </p>
          </div>
        )}

        {/* ── Sub-view routing ── */}
        {view === 'pursue' && (
          <PursueView onBack={() => setView('main')} />
        )}

        {view === 'disclaimer' && (
          <DisclaimerView
            onBack={() => setView('main')}
            onOpenPrivacy={() => setShowPrivacy(true)}
          />
        )}

        {/* ── Main view ── */}
        {view === 'main' && (
          <>
            {/* Scrollable body */}
            <div style={{
              flex: 1, minHeight: 0,
              overflowY: 'scroll',
              WebkitOverflowScrolling: 'touch',
              padding: '20px 16px 36px',
              display: 'flex', flexDirection: 'column', gap: '14px',
            }}>

              {/* Opening statement */}
              <div style={{ padding: '2px 0' }}>
                <p style={{
                  ...pStyle,
                  fontFamily: 'Georgia, serif', color: '#14532d',
                  fontWeight: '600', fontStyle: 'italic', marginBottom: '12px',
                }}>
                  "All we like sheep have gone astray"
                  <span style={{ fontFamily: 'inherit', fontStyle: 'normal', color: '#2d4a2a', fontWeight: '400' }}>
                    {' '}— yet our Shepherd sought us, found us, and called us by name. Once we were sheep
                    who heard His voice, we became His own. Now, having been shepherded, we are called to
                    shepherd others. This is Sheepheard.
                  </span>
                </p>
                <p style={pStyle}>
                  Sheepheard is a faith-centred discipleship companion built to walk alongside you in
                  your journey toward deeper maturity and intimacy — with God and with the people He has
                  placed in your life.
                </p>
                <p style={{ ...pStyle, margin: 0, color: '#5a7055' }}>
                  We believe discipleship is not a program but a way of life: a continuous, Spirit-led
                  movement from being known by the Shepherd to becoming a shepherding presence for others.
                  Sheepheard exists to support that movement — so that{' '}
                  <strong style={{ color: '#14532d' }}>no sheep walks alone.</strong>
                </p>
              </div>

              {/* Nav cards — replace collapsible sections */}
              <NavCard
                icon="✦"
                label="What We Pursue"
                subtitle="Three core commitments"
                onClick={() => setView('pursue')}
              />
              <NavCard
                icon="⚠"
                label="Disclaimer"
                subtitle="Please read carefully"
                onClick={() => setView('disclaimer')}
              />

              {/* Footer verse */}
              <div style={{ textAlign: 'center', padding: '4px 0 0' }}>
                <p style={{
                  margin: '0 0 4px', fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: '1rem', color: '#4a6741',
                }}>
                  "He restores my soul." — Psalm 23:3
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#9aad94', letterSpacing: '0.04em' }}>
                  Sheepheard · A Discipleship Companion · Built for His Flock
                </p>
              </div>
            </div>

            {/* Sticky footer */}
            <div style={{
              padding: '13px 18px',
              background: '#ffffff',
              borderTop: '1px solid #d6e4d0',
              flexShrink: 0,
            }}>
              <button
                onClick={onClose}
                style={{
                  width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
                  background: 'linear-gradient(135deg, #4d7c0f, #166534)',
                  color: 'white', fontSize: '1.05rem', fontWeight: '800',
                  cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em',
                }}
              >
                {closeLabel}
              </button>
            </div>
          </>
        )}

        {/* Back button footer for sub-views */}
        {view !== 'main' && (
          <div style={{
            padding: '13px 18px',
            background: '#ffffff',
            borderTop: '1px solid #d6e4d0',
            flexShrink: 0,
          }}>
            <button
              onClick={() => setView('main')}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
                background: 'linear-gradient(135deg, #4d7c0f, #166534)',
                color: 'white', fontSize: '1.05rem', fontWeight: '800',
                cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em',
              }}
            >
              ← Back to Purpose
            </button>
          </div>
        )}
      </div>

      {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
    </div>
  );
}